<template>
  <div v-if="showBanner" :class="['subscription-banner', `banner-${variant}`]">
    <div class="d-flex align-items-center justify-content-center" style="gap: 0.5rem">
      <feather-icon :icon="bannerIcon" size="14" />
      <span>{{ bannerMessage }}</span>
      <b-button
        v-if="showActionButton"
        variant="light"
        size="sm"
        class="sub-banner-btn"
        @click="goToSubscription"
      >
        {{ actionLabel }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, watch, onMounted } from '@vue/composition-api'
import { BButton } from 'bootstrap-vue'
import { useAuthStore } from '@/pinia/auth'
import { useWorkspaceStore } from '@/pinia/workspace'

export default defineComponent({
  name: 'SubscriptionBanner',
  components: { BButton },
  setup(props, { root }) {
    const authStore = useAuthStore()
    const workspaceStore = useWorkspaceStore()

    const subscriptionStatus = computed(() => authStore.subscriptionStatus)
    const isOwner = computed(() => workspaceStore.isOwner)

    const showBanner = computed(() => {
      return ['warning', 'grace', 'paused'].includes(subscriptionStatus.value)
    })

    const variant = computed(() => {
      if (subscriptionStatus.value === 'warning') return 'warning'
      if (subscriptionStatus.value === 'grace') return 'danger'
      if (subscriptionStatus.value === 'paused') return 'info'
      return ''
    })

    const bannerIcon = computed(() => {
      if (subscriptionStatus.value === 'warning') return 'AlertTriangleIcon'
      if (subscriptionStatus.value === 'paused') return 'PauseCircleIcon'
      return 'AlertOctagonIcon'
    })

    const bannerMessage = computed(() => {
      if (subscriptionStatus.value === 'warning') {
        return 'Tagihan langganan belum dibayar. Segera perpanjang untuk menghindari pembatasan akses.'
      }
      if (subscriptionStatus.value === 'grace') {
        return 'Masa tenggang aktif. Akses dalam mode baca saja — perpanjang untuk akses penuh.'
      }
      if (subscriptionStatus.value === 'paused') {
        if (isOwner.value) {
          return 'Langganan sedang dijeda. Akses dalam mode baca saja.'
        }
        return 'Langganan sedang dijeda. Hubungi pemilik fasilitas atau Dekacare support untuk mengaktifkan kembali.'
      }
      return ''
    })

    // Only show action button for owner (or non-paused statuses which are always owner-relevant)
    const showActionButton = computed(() => {
      if (subscriptionStatus.value === 'paused') return isOwner.value
      return true
    })

    const actionLabel = computed(() => {
      if (subscriptionStatus.value === 'paused') return 'Aktifkan Kembali'
      return 'Perpanjang Sekarang'
    })

    const goToSubscription = () => {
      root.$router.push({ name: 'workspace', query: { tab: 'berlangganan' } })
    }

    // Lazily check owner status when banner is shown for paused state
    const checkOwnerIfNeeded = async () => {
      if (subscriptionStatus.value === 'paused' && !workspaceStore.ownerStatusChecked) {
        try {
          await workspaceStore.checkOwnerStatus()
        } catch (e) {
          // Silently fail — defaults to non-owner (no action button shown)
        }
      }
    }

    watch(subscriptionStatus, checkOwnerIfNeeded, { immediate: false })
    onMounted(checkOwnerIfNeeded)

    return {
      showBanner,
      variant,
      bannerIcon,
      bannerMessage,
      showActionButton,
      actionLabel,
      goToSubscription,
    }
  },
})
</script>

<style lang="scss" scoped>
.subscription-banner {
  // Pin flush against the top edge of the fixed footer so the banner reads as
  // part of the footer instead of floating in the content flow.
  // `bottom` matches Vuexy's $footer-height (3.35rem) + the footer's z-index (1030).
  position: fixed;
  right: 0;
  bottom: 3.35rem;
  left: 0;
  z-index: 1031;
  padding: 0.35rem 1rem;
  font-size: 0.78rem;
  font-weight: 500;
  text-align: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);

  &.banner-warning {
    background: #fff3e0;
    color: #e65100;
    border-top: 1px solid rgba(255, 159, 67, 0.3);
  }

  &.banner-danger {
    background: #fce4ec;
    color: #c62828;
    border-top: 1px solid rgba(234, 84, 85, 0.3);
  }

  &.banner-info {
    background: #e3f2fd;
    color: #1565c0;
    border-top: 1px solid rgba(0, 123, 255, 0.2);
  }
}

.sub-banner-btn {
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 4px;
}
</style>
